/**
 * VoiceConfig Deployment Settings Schema
 *
 * SHARED realtime-voice model configuration, factored out of the phone_call
 * (TWILIO_VOICE) settings so non-telephony deployments (e.g. the embed web
 * widget "call" feature) can expose the same model/voice/turn-detection knobs
 * WITHOUT the phone-specific fields (phone number, transfers, SMS, whitelist,
 * Twilio region).
 *
 * Field names mirror the phone_call settings exactly so the voice-relay context
 * builder maps both sources through the same logic.
 *
 * Leaf schemas (model registry, turn detection, session timeout, flow) are
 * imported from ./Twilio/* — the single source of truth — and reused verbatim.
 */

import { z } from "zod";

import {
  GptLiveDelegationModelSchema,
  GptLiveNativeModeSchema,
  TtsProviderSchema,
  VOICE_PROVIDERS
} from "./Twilio/__json.schema";
import {
  DEFAULT_GEMINI_LIVE_MODEL,
  GeminiLiveModelSettingSchema
} from "./Twilio/GeminiLiveModels.schema";
import { GeminiThinkingLevelChosenAtSchema } from "./Twilio/GeminiLiveThinkingLevelChoice.schema";
import { GeminiCustomVocabularySchema } from "./Twilio/GeminiLiveTranscription.schema";
import { GEMINI_LIVE_THINKING_LEVELS } from "./Twilio/GeminiLiveTunables.schema";
import { GeminiLiveTurnDetectionSchema } from "./Twilio/GeminiLiveTurnDetection.schema";
import {
  DEFAULT_GREETING_PLAYBACK,
  GreetingPlaybackSettingSchema
} from "./Twilio/GreetingPlayback.schema";
import {
  DEFAULT_OPENAI_REALTIME_FEATURE_SETTINGS,
  DEFAULT_OPENAI_REALTIME_MODEL,
  OPENAI_REALTIME_MODELS,
  OpenAIRealtimeFeatureSettingsSchema,
  OpenAIRealtimeModelIdSchema,
  type OpenAIRealtimeVoice
} from "./Twilio/OpenAIRealtimeModels.schema";
import {
  DEFAULT_OPENAI_REALTIME_TURN_DETECTION,
  OpenAIRealtimeTurnDetectionSchema
} from "./Twilio/OpenAIRealtimeTurnDetection.schema";
import { PhoneCallFlowSchema } from "./Twilio/PhoneCallFlow.schema";
import {
  DEFAULT_PHONE_CALL_SESSION_TIMEOUT,
  PhoneCallSessionTimeoutSchema
} from "./Twilio/PhoneCallSessionTimeout.schema";

/**
 * Shared realtime-voice configuration.
 *
 * Exactly one turn-detection field is consulted at runtime, picked by `provider`:
 *   provider === "openai" → openaiTurnDetection
 *   provider === "gemini" → geminiTurnDetection
 * Both are optional so callers fall back to DEFAULT_*_TURN_DETECTION when missing.
 */
export const VoiceConfigSchema = z.object({
  // Voice AI provider for the realtime session. undefined = "openai" (back-compat).
  provider: z.enum(VOICE_PROVIDERS).optional().catch(undefined),
  // Free-form legacy model label; new code reads `openaiModel` / `geminiModel`.
  model: z.string().optional(),
  // Selected OpenAI Realtime model — validated against the registry; unknown
  // legacy values normalize to undefined (relay falls back to the default).
  openaiModel: OpenAIRealtimeModelIdSchema.optional().catch(undefined),
  // Per-deployment OpenAI feature toggles (preambles, reasoning, etc.).
  // Capability-gated at runtime — flags for unsupported models are ignored.
  openaiSettings: OpenAIRealtimeFeatureSettingsSchema.optional(),
  // Selected Gemini Live model. Absent on every row written before it existed;
  // those resolve to 3.1 with no backfill — see GeminiLiveModelSettingSchema.
  geminiModel: GeminiLiveModelSettingSchema,
  // TTS provider — only "openai" is wired through the voice relay today.
  ttsProvider: TtsProviderSchema,
  // TTS voice identifier (e.g. "marin", "alloy").
  voiceId: z.string(),
  // BCP-47 language code (e.g. "en-US") for accent control.
  language: z.string(),
  // Gemini thinking level — reasoning depth vs latency. Only used for provider="gemini".
  // Sent only beside `geminiThinkingLevelChosenAt` — see readGeminiThinkingLevelChoice.
  geminiThinkingLevel: z.enum(GEMINI_LIVE_THINKING_LEVELS).optional(),
  // When the level above was picked through a surface that sends it. A level
  // stored without it predates the send, and stays unsent.
  geminiThinkingLevelChosenAt: GeminiThinkingLevelChosenAtSchema,
  // Phrases Gemini's input transcription is biased toward. Only used for
  // provider="gemini". Absent = no bias.
  geminiCustomVocabulary: GeminiCustomVocabularySchema,
  // The Responses-API model a GPT-Live session delegates tool calls and
  // reasoning to. Required when provider = "gpt-live", enforced on write by
  // `requireGptLiveDelegationModel`; see GptLiveDelegationModelSchema for why
  // the read path stays lenient.
  gptLiveDelegationModel: GptLiveDelegationModelSchema,
  // Speech-to-speech instead of the Responses backend, per deployment.
  // undefined = off. See GptLiveNativeModeSchema for why the mode is fixed at
  // session start and cannot be changed mid-call.
  gptLiveNativeMode: GptLiveNativeModeSchema,
  // Provider-native turn detection. Adapters read their own field.
  openaiTurnDetection: OpenAIRealtimeTurnDetectionSchema.optional(),
  geminiTurnDetection: GeminiLiveTurnDetectionSchema.optional(),
  // Session limits (max duration / idle).
  session_timeout: PhoneCallSessionTimeoutSchema,
  // Conversation behavior — barge-in / interruption / response delay.
  flow: PhoneCallFlowSchema,
  // First message the agent speaks when the call connects.
  initialMessage: z.string().optional(),
  // How that greeting reaches the caller. Absent on every config written before
  // it existed; those resolve to "live" — see GreetingPlaybackSettingSchema.
  greetingPlayback: GreetingPlaybackSettingSchema
});

export type VoiceConfigSettings = z.infer<typeof VoiceConfigSchema>;

/** Default voice — sourced from the registry default model so model + voice stay aligned. */
const DEFAULT_VOICE: OpenAIRealtimeVoice =
  OPENAI_REALTIME_MODELS[DEFAULT_OPENAI_REALTIME_MODEL].defaultVoice;

export const DEFAULT_VOICE_CONFIG: VoiceConfigSettings = {
  language: "en-US",
  ttsProvider: "openai",
  voiceId: DEFAULT_VOICE,
  openaiModel: DEFAULT_OPENAI_REALTIME_MODEL,
  openaiSettings: DEFAULT_OPENAI_REALTIME_FEATURE_SETTINGS,
  geminiModel: DEFAULT_GEMINI_LIVE_MODEL,
  greetingPlayback: DEFAULT_GREETING_PLAYBACK,
  openaiTurnDetection: DEFAULT_OPENAI_REALTIME_TURN_DETECTION,
  // No `geminiTurnDetection`: an absent value follows the deployment's MODEL
  // (`resolveGeminiLiveTurnDetection`), through a later model switch included.
  session_timeout: DEFAULT_PHONE_CALL_SESSION_TIMEOUT,
  flow: {
    user_start_first: false,
    interruption: { allowed: true, keep_interruption_message: false },
    response_delay: 0
  }
};
